import { lazy } from 'react';
import { Navigate, Routes, type RouteObject } from 'react-router';

import RouteSuspense from '@/components/suspenses/route-suspense/route-suspense';

import { APP_ROUTE } from './app.route.constant';
import { renderRoutes } from '@/libs/react-router';
import AppSuspenseFallback from '@/components/suspenses/app-suspense-fallback/app-suspense-fallback';

const {
  APP,
  AUTH,
  ANALYTICS,
  SITE_SETTING,
  COMPANY_INFO,
  COMPANY_STAT,
  COMPANY_FEATURE,
  CLIENT,
  HERO_SLIDE,
  PRODUCT_CATEGORY,
  PRODUCT,
  BLOG_CATEGORY,
  BLOG_POST,
  CONTACT_MESSAGE,
  MEDIA,
  USER,
  ERROR,
  ROUTE_NOT_FOUND,
} = APP_ROUTE;

// Layouts
const AuthLayout = lazy(
  () => import('@/components/layouts/auth-layout/auth-layout')
);
const AppLayout = lazy(
  () => import('@/components/layouts/app-layout/app-layout')
);

// Auth
const SigninPage = lazy(() => import('@/app/auths/signin-page/signin-page'));

// Dashboard
const DashboardPage = lazy(
  () => import('@/app/dashboards/dashboard-page/dashboard-page')
);

// Settings (singletons)
const SiteSettingPage = lazy(
  () => import('@/app/site-settings/site-setting-page/site-setting-page')
);
const CompanyInfoPage = lazy(
  () => import('@/app/company-info/company-info-page/company-info-page')
);

// Clients
const ListClientPage = lazy(
  () => import('@/app/clients/list-client-page/list-client-page')
);
const CreateClientPage = lazy(
  () => import('@/app/clients/create-client-page/create-client-page')
);
const EditClientPage = lazy(
  () => import('@/app/clients/edit-client-page/edit-client-page')
);

// Hero slides
const ListHeroSlidePage = lazy(
  () => import('@/app/hero-slides/list-hero-slide-page/list-hero-slide-page')
);
const CreateHeroSlidePage = lazy(
  () =>
    import('@/app/hero-slides/create-hero-slide-page/create-hero-slide-page')
);
const EditHeroSlidePage = lazy(
  () => import('@/app/hero-slides/edit-hero-slide-page/edit-hero-slide-page')
);

// Company stats
const ListCompanyStatPage = lazy(
  () =>
    import('@/app/company-stats/list-company-stat-page/list-company-stat-page')
);
const CreateCompanyStatPage = lazy(
  () =>
    import('@/app/company-stats/create-company-stat-page/create-company-stat-page')
);
const EditCompanyStatPage = lazy(
  () =>
    import('@/app/company-stats/edit-company-stat-page/edit-company-stat-page')
);

// Company features
const ListCompanyFeaturePage = lazy(
  () =>
    import('@/app/company-features/list-company-feature-page/list-company-feature-page')
);
const CreateCompanyFeaturePage = lazy(
  () =>
    import('@/app/company-features/create-company-feature-page/create-company-feature-page')
);
const EditCompanyFeaturePage = lazy(
  () =>
    import('@/app/company-features/edit-company-feature-page/edit-company-feature-page')
);

// Product categories
const ListProductCategoryPage = lazy(
  () =>
    import('@/app/product-categories/list-product-category-page/list-product-category-page')
);
const CreateProductCategoryPage = lazy(
  () =>
    import('@/app/product-categories/create-product-category-page/create-product-category-page')
);
const EditProductCategoryPage = lazy(
  () =>
    import('@/app/product-categories/edit-product-category-page/edit-product-category-page')
);

// Products
const ListProductPage = lazy(
  () => import('@/app/products/list-product-page/list-product-page')
);
const CreateProductPage = lazy(
  () => import('@/app/products/create-product-page/create-product-page')
);
const EditProductPage = lazy(
  () => import('@/app/products/edit-product-page/edit-product-page')
);

// Blog categories
const ListBlogCategoryPage = lazy(
  () =>
    import('@/app/blog-categories/list-blog-category-page/list-blog-category-page')
);
const CreateBlogCategoryPage = lazy(
  () =>
    import('@/app/blog-categories/create-blog-category-page/create-blog-category-page')
);
const EditBlogCategoryPage = lazy(
  () =>
    import('@/app/blog-categories/edit-blog-category-page/edit-blog-category-page')
);

// Blog posts
const ListBlogPostPage = lazy(
  () => import('@/app/blog-posts/list-blog-post-page/list-blog-post-page')
);
const CreateBlogPostPage = lazy(
  () => import('@/app/blog-posts/create-blog-post-page/create-blog-post-page')
);
const EditBlogPostPage = lazy(
  () => import('@/app/blog-posts/edit-blog-post-page/edit-blog-post-page')
);

// Contact messages
const ListContactMessagePage = lazy(
  () =>
    import('@/app/contact-messages/list-contact-message-page/list-contact-message-page')
);
const ContactMessageDetailPage = lazy(
  () =>
    import('@/app/contact-messages/contact-message-detail-page/contact-message-detail-page')
);

// Media
const ListMediaPage = lazy(
  () => import('@/app/media/list-media-page/list-media-page')
);

// Users
const ListUserPage = lazy(
  () => import('@/app/users/list-user-page/list-user-page')
);
const CreateUserPage = lazy(
  () => import('@/app/users/create-user-page/create-user-page')
);
const EditUserPage = lazy(
  () => import('@/app/users/edit-user-page/edit-user-page')
);

// Errors
const NotFoundPage = lazy(
  () => import('@/app/errors/not-found-page/not-found-page')
);
const ForbiddenPage = lazy(
  () => import('@/app/errors/forbidden-page/forbidden-page')
);
const MaintenancePage = lazy(
  () => import('@/app/errors/maintenance-page/maintenance-page')
);

export const routes: RouteObject[] = [
  {
    path: AUTH.ROOT.path,
    element: <RouteSuspense component={AuthLayout} />,
    children: [
      {
        index: true,
        element: <Navigate to={AUTH.SIGN_IN.getHref()} />,
      },
      {
        path: AUTH.SIGN_IN.path,
        element: <RouteSuspense component={SigninPage} />,
      },
    ],
  },

  {
    path: APP.ROOT.path,
    element: (
      <RouteSuspense
        component={AppLayout}
        suspenseFallback={<AppSuspenseFallback />}
      />
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ANALYTICS.DASHBOARD.getHref()} />,
      },
      {
        path: ANALYTICS.DASHBOARD.path,
        element: <RouteSuspense component={DashboardPage} />,
      },

      // Settings singletons
      {
        path: SITE_SETTING.ROOT.path,
        element: <RouteSuspense component={SiteSettingPage} />,
      },
      {
        path: COMPANY_INFO.ROOT.path,
        element: <RouteSuspense component={CompanyInfoPage} />,
      },

      // Clients
      {
        path: CLIENT.LIST.path,
        element: <RouteSuspense component={ListClientPage} />,
      },
      {
        path: CLIENT.CREATE.path,
        element: <RouteSuspense component={CreateClientPage} />,
      },
      {
        path: CLIENT.EDIT.path,
        element: <RouteSuspense component={EditClientPage} />,
      },

      // Hero slides
      {
        path: HERO_SLIDE.LIST.path,
        element: <RouteSuspense component={ListHeroSlidePage} />,
      },
      {
        path: HERO_SLIDE.CREATE.path,
        element: <RouteSuspense component={CreateHeroSlidePage} />,
      },
      {
        path: HERO_SLIDE.EDIT.path,
        element: <RouteSuspense component={EditHeroSlidePage} />,
      },

      // Company stats
      {
        path: COMPANY_STAT.LIST.path,
        element: <RouteSuspense component={ListCompanyStatPage} />,
      },
      {
        path: COMPANY_STAT.CREATE.path,
        element: <RouteSuspense component={CreateCompanyStatPage} />,
      },
      {
        path: COMPANY_STAT.EDIT.path,
        element: <RouteSuspense component={EditCompanyStatPage} />,
      },

      // Company features
      {
        path: COMPANY_FEATURE.LIST.path,
        element: <RouteSuspense component={ListCompanyFeaturePage} />,
      },
      {
        path: COMPANY_FEATURE.CREATE.path,
        element: <RouteSuspense component={CreateCompanyFeaturePage} />,
      },
      {
        path: COMPANY_FEATURE.EDIT.path,
        element: <RouteSuspense component={EditCompanyFeaturePage} />,
      },

      // Product categories
      {
        path: PRODUCT_CATEGORY.LIST.path,
        element: <RouteSuspense component={ListProductCategoryPage} />,
      },
      {
        path: PRODUCT_CATEGORY.CREATE.path,
        element: <RouteSuspense component={CreateProductCategoryPage} />,
      },
      {
        path: PRODUCT_CATEGORY.EDIT.path,
        element: <RouteSuspense component={EditProductCategoryPage} />,
      },

      // Products
      {
        path: PRODUCT.LIST.path,
        element: <RouteSuspense component={ListProductPage} />,
      },
      {
        path: PRODUCT.CREATE.path,
        element: <RouteSuspense component={CreateProductPage} />,
      },
      {
        path: PRODUCT.EDIT.path,
        element: <RouteSuspense component={EditProductPage} />,
      },

      // Blog categories
      {
        path: BLOG_CATEGORY.LIST.path,
        element: <RouteSuspense component={ListBlogCategoryPage} />,
      },
      {
        path: BLOG_CATEGORY.CREATE.path,
        element: <RouteSuspense component={CreateBlogCategoryPage} />,
      },
      {
        path: BLOG_CATEGORY.EDIT.path,
        element: <RouteSuspense component={EditBlogCategoryPage} />,
      },

      // Blog posts
      {
        path: BLOG_POST.LIST.path,
        element: <RouteSuspense component={ListBlogPostPage} />,
      },
      {
        path: BLOG_POST.CREATE.path,
        element: <RouteSuspense component={CreateBlogPostPage} />,
      },
      {
        path: BLOG_POST.EDIT.path,
        element: <RouteSuspense component={EditBlogPostPage} />,
      },

      // Contact messages
      // {
      //   path: CONTACT_MESSAGE.LIST.path,
      //   element: <RouteSuspense component={ListContactMessagePage} />,
      // },
      {
        path: CONTACT_MESSAGE.DETAIL.path,
        element: <RouteSuspense component={ContactMessageDetailPage} />,
      },

      // Media
      {
        path: MEDIA.LIST.path,
        element: <RouteSuspense component={ListMediaPage} />,
      },

      // Users
      {
        path: USER.LIST.path,
        element: <RouteSuspense component={ListUserPage} />,
      },
      {
        path: USER.CREATE.path,
        element: <RouteSuspense component={CreateUserPage} />,
      },
      {
        path: USER.EDIT.path,
        element: <RouteSuspense component={EditUserPage} />,
      },
    ],
  },

  {
    path: ERROR.NOT_FOUND.path,
    element: <RouteSuspense component={NotFoundPage} />,
  },
  {
    path: ERROR.FORBIDDEN.path,
    element: <RouteSuspense component={ForbiddenPage} />,
  },
  {
    path: ERROR.MAINTENANCE.path,
    element: <RouteSuspense component={MaintenancePage} />,
  },

  {
    path: ROUTE_NOT_FOUND.path,
    element: <Navigate to={ERROR.NOT_FOUND.getHref()} />,
  },
];

export default function AppRoute() {
  return <Routes>{renderRoutes(routes)}</Routes>;
}
